// Shared server/client catalogue; the six prescriptions below move unchanged.
import { normalizeLang, type Lang } from "../../../src/i18n/locale.ts";
import { FITNESS_CURATED_PLAN_LABELS } from "./fitnessCuratedPlanLabels.ts";
export { FITNESS_CURATED_PLAN_LABELS };
/**
 * Curated fitness plans library.
 * 6 plans covering beginner→advanced × weight-loss/hypertrophy/strength/performance.
 */

export type FitnessLevel = "beginner" | "intermediate" | "advanced";
export type FitnessGoal = "weight_loss" | "hypertrophy" | "strength" | "performance";

export interface PlanExercise {
  name: string;
  sets: number;
  reps: string;
  rest: number; // seconds
  muscle: string;
}

export interface PlanDay {
  name: string;
  i18nKey: string;
  exercises: PlanExercise[];
}

export interface CuratedPlan {
  id: string;
  i18nKey: string;
  level: FitnessLevel;
  goal: FitnessGoal;
  daysPerWeek: number;
  days: PlanDay[];
  progressionRule: string; // i18n key for progression description
}

// ── Plan 1: Beginner — Weight Loss ──────────────────────────────────────────

const BEGINNER_WEIGHT_LOSS: CuratedPlan = {
  id: "beginner_weight_loss",
  i18nKey: "fitness.plan.beginnerWeightLoss",
  level: "beginner",
  goal: "weight_loss",
  daysPerWeek: 3,
  progressionRule: "fitness.plan.progression.plusOneRep",
  days: [
    {
      name: "Full Body A", i18nKey: "fitness.plan.day.fullBodyA",
      exercises: [
        { name: "Squat", sets: 3, reps: "12-15", rest: 60, muscle: "legs" },
        { name: "Push-up", sets: 3, reps: "12-15", rest: 60, muscle: "chest" },
        { name: "Dumbbell Row", sets: 3, reps: "12-15", rest: 60, muscle: "back" },
        { name: "Plank", sets: 3, reps: "30s", rest: 45, muscle: "core" },
        { name: "Jumping Jacks", sets: 3, reps: "30s", rest: 30, muscle: "cardio" },
      ],
    },
    {
      name: "Full Body B", i18nKey: "fitness.plan.day.fullBodyB",
      exercises: [
        { name: "Lunges", sets: 3, reps: "12-15", rest: 60, muscle: "legs" },
        { name: "Mountain Climbers", sets: 3, reps: "30s", rest: 45, muscle: "cardio" },
        { name: "Glute Bridge", sets: 3, reps: "15", rest: 60, muscle: "glutes" },
        { name: "Push-up", sets: 3, reps: "12-15", rest: 60, muscle: "chest" },
        { name: "Dumbbell Row", sets: 3, reps: "12-15", rest: 60, muscle: "back" },
      ],
    },
    {
      name: "Full Body C", i18nKey: "fitness.plan.day.fullBodyC",
      exercises: [
        { name: "Squat", sets: 3, reps: "15", rest: 60, muscle: "legs" },
        { name: "Plank", sets: 3, reps: "45s", rest: 45, muscle: "core" },
        { name: "Jumping Jacks", sets: 3, reps: "45s", rest: 30, muscle: "cardio" },
        { name: "Lunges", sets: 3, reps: "12", rest: 60, muscle: "legs" },
        { name: "Glute Bridge", sets: 3, reps: "15", rest: 60, muscle: "glutes" },
      ],
    },
  ],
};

// ── Plan 2: Beginner — Hypertrophy ──────────────────────────────────────────

const BEGINNER_HYPERTROPHY: CuratedPlan = {
  id: "beginner_hypertrophy",
  i18nKey: "fitness.plan.beginnerHypertrophy",
  level: "beginner",
  goal: "hypertrophy",
  daysPerWeek: 4,
  progressionRule: "fitness.plan.progression.plus2_5kg",
  days: [
    {
      name: "Push", i18nKey: "fitness.plan.day.push",
      exercises: [
        { name: "Bench Press", sets: 3, reps: "10-12", rest: 90, muscle: "chest" },
        { name: "Incline Dumbbell Press", sets: 3, reps: "10-12", rest: 90, muscle: "chest" },
        { name: "Overhead Press", sets: 3, reps: "10-12", rest: 90, muscle: "shoulders" },
        { name: "Lateral Raise", sets: 3, reps: "12-15", rest: 60, muscle: "shoulders" },
        { name: "Tricep Pushdown", sets: 3, reps: "12", rest: 60, muscle: "triceps" },
      ],
    },
    {
      name: "Pull", i18nKey: "fitness.plan.day.pull",
      exercises: [
        { name: "Barbell Row", sets: 3, reps: "10-12", rest: 90, muscle: "back" },
        { name: "Lat Pulldown", sets: 3, reps: "10-12", rest: 90, muscle: "back" },
        { name: "Face Pull", sets: 3, reps: "15", rest: 60, muscle: "shoulders" },
        { name: "Barbell Curl", sets: 3, reps: "10-12", rest: 60, muscle: "biceps" },
        { name: "Hammer Curl", sets: 3, reps: "12", rest: 60, muscle: "biceps" },
      ],
    },
    {
      name: "Legs", i18nKey: "fitness.plan.day.legs",
      exercises: [
        { name: "Squat", sets: 3, reps: "10-12", rest: 90, muscle: "quads" },
        { name: "Romanian Deadlift", sets: 3, reps: "10-12", rest: 90, muscle: "hamstrings" },
        { name: "Leg Press", sets: 3, reps: "12", rest: 90, muscle: "quads" },
        { name: "Leg Curl", sets: 3, reps: "12", rest: 60, muscle: "hamstrings" },
        { name: "Calf Raise", sets: 3, reps: "15", rest: 60, muscle: "calves" },
      ],
    },
    {
      name: "Full Body", i18nKey: "fitness.plan.day.fullBody",
      exercises: [
        { name: "Deadlift", sets: 3, reps: "8-10", rest: 120, muscle: "back" },
        { name: "Dumbbell Press", sets: 3, reps: "10-12", rest: 90, muscle: "chest" },
        { name: "Dumbbell Row", sets: 3, reps: "10-12", rest: 90, muscle: "back" },
        { name: "Squat", sets: 3, reps: "10-12", rest: 90, muscle: "legs" },
        { name: "Plank", sets: 3, reps: "45s", rest: 45, muscle: "core" },
      ],
    },
  ],
};

// ── Plan 3: Intermediate — Strength ─────────────────────────────────────────

const INTERMEDIATE_STRENGTH: CuratedPlan = {
  id: "intermediate_strength",
  i18nKey: "fitness.plan.intermediateStrength",
  level: "intermediate",
  goal: "strength",
  daysPerWeek: 4,
  progressionRule: "fitness.plan.progression.plus5kg",
  days: [
    {
      name: "Upper A", i18nKey: "fitness.plan.day.upperA",
      exercises: [
        { name: "Bench Press", sets: 4, reps: "6-8", rest: 180, muscle: "chest" },
        { name: "Barbell Row", sets: 4, reps: "6-8", rest: 180, muscle: "back" },
        { name: "Overhead Press", sets: 4, reps: "6-8", rest: 150, muscle: "shoulders" },
        { name: "Weighted Pull-up", sets: 4, reps: "6-8", rest: 150, muscle: "back" },
        { name: "Barbell Curl", sets: 3, reps: "8-10", rest: 90, muscle: "biceps" },
      ],
    },
    {
      name: "Lower A", i18nKey: "fitness.plan.day.lowerA",
      exercises: [
        { name: "Squat", sets: 4, reps: "6-8", rest: 180, muscle: "quads" },
        { name: "Romanian Deadlift", sets: 4, reps: "6-8", rest: 180, muscle: "hamstrings" },
        { name: "Leg Press", sets: 4, reps: "8-10", rest: 120, muscle: "quads" },
        { name: "Leg Curl", sets: 3, reps: "10", rest: 90, muscle: "hamstrings" },
        { name: "Calf Raise", sets: 4, reps: "12", rest: 60, muscle: "calves" },
      ],
    },
    {
      name: "Upper B", i18nKey: "fitness.plan.day.upperB",
      exercises: [
        { name: "Incline Bench Press", sets: 4, reps: "6-8", rest: 180, muscle: "chest" },
        { name: "Weighted Pull-up", sets: 4, reps: "6-8", rest: 180, muscle: "back" },
        { name: "Dumbbell Press", sets: 3, reps: "8-10", rest: 120, muscle: "shoulders" },
        { name: "Cable Row", sets: 3, reps: "8-10", rest: 120, muscle: "back" },
        { name: "Tricep Pushdown", sets: 3, reps: "10-12", rest: 60, muscle: "triceps" },
      ],
    },
    {
      name: "Lower B", i18nKey: "fitness.plan.day.lowerB",
      exercises: [
        { name: "Deadlift", sets: 4, reps: "5-6", rest: 240, muscle: "back" },
        { name: "Front Squat", sets: 4, reps: "6-8", rest: 180, muscle: "quads" },
        { name: "Walking Lunges", sets: 3, reps: "10/leg", rest: 120, muscle: "legs" },
        { name: "Glute Bridge", sets: 3, reps: "12", rest: 90, muscle: "glutes" },
        { name: "Ab Wheel", sets: 3, reps: "10", rest: 60, muscle: "core" },
      ],
    },
  ],
};

// ── Plan 4: Intermediate — Hypertrophy ──────────────────────────────────────

const INTERMEDIATE_HYPERTROPHY: CuratedPlan = {
  id: "intermediate_hypertrophy",
  i18nKey: "fitness.plan.intermediateHypertrophy",
  level: "intermediate",
  goal: "hypertrophy",
  daysPerWeek: 5,
  progressionRule: "fitness.plan.progression.plus2_5kgWeekly",
  days: [
    {
      name: "Push", i18nKey: "fitness.plan.day.push",
      exercises: [
        { name: "Bench Press", sets: 4, reps: "8-10", rest: 90, muscle: "chest" },
        { name: "Incline Dumbbell Press", sets: 4, reps: "10-12", rest: 90, muscle: "chest" },
        { name: "Cable Flyes", sets: 3, reps: "12-15", rest: 60, muscle: "chest" },
        { name: "Overhead Press", sets: 4, reps: "8-10", rest: 90, muscle: "shoulders" },
        { name: "Lateral Raise", sets: 4, reps: "15", rest: 45, muscle: "shoulders" },
        { name: "Tricep Pushdown", sets: 3, reps: "12", rest: 60, muscle: "triceps" },
      ],
    },
    {
      name: "Pull", i18nKey: "fitness.plan.day.pull",
      exercises: [
        { name: "Barbell Row", sets: 4, reps: "8-10", rest: 90, muscle: "back" },
        { name: "Lat Pulldown", sets: 4, reps: "10-12", rest: 90, muscle: "lats" },
        { name: "Cable Row", sets: 3, reps: "12", rest: 90, muscle: "back" },
        { name: "Face Pull", sets: 3, reps: "15", rest: 60, muscle: "shoulders" },
        { name: "Barbell Curl", sets: 3, reps: "10-12", rest: 60, muscle: "biceps" },
        { name: "Hammer Curl", sets: 3, reps: "12", rest: 60, muscle: "biceps" },
      ],
    },
    {
      name: "Legs", i18nKey: "fitness.plan.day.legs",
      exercises: [
        { name: "Squat", sets: 4, reps: "8-10", rest: 120, muscle: "quads" },
        { name: "Romanian Deadlift", sets: 4, reps: "10-12", rest: 90, muscle: "hamstrings" },
        { name: "Leg Press", sets: 4, reps: "12", rest: 90, muscle: "quads" },
        { name: "Leg Curl", sets: 3, reps: "12", rest: 60, muscle: "hamstrings" },
        { name: "Leg Extension", sets: 3, reps: "12-15", rest: 60, muscle: "quads" },
        { name: "Calf Raise", sets: 4, reps: "15", rest: 45, muscle: "calves" },
      ],
    },
    {
      name: "Upper", i18nKey: "fitness.plan.day.upper",
      exercises: [
        { name: "Incline Bench Press", sets: 4, reps: "8-10", rest: 90, muscle: "chest" },
        { name: "Weighted Pull-up", sets: 4, reps: "8-10", rest: 90, muscle: "back" },
        { name: "Dumbbell Press", sets: 3, reps: "10-12", rest: 90, muscle: "shoulders" },
        { name: "Cable Row", sets: 3, reps: "10-12", rest: 90, muscle: "back" },
        { name: "Lateral Raise", sets: 3, reps: "15", rest: 45, muscle: "shoulders" },
      ],
    },
    {
      name: "Lower", i18nKey: "fitness.plan.day.lower",
      exercises: [
        { name: "Front Squat", sets: 4, reps: "8-10", rest: 120, muscle: "quads" },
        { name: "Deadlift", sets: 4, reps: "6-8", rest: 180, muscle: "back" },
        { name: "Walking Lunges", sets: 3, reps: "12/leg", rest: 90, muscle: "legs" },
        { name: "Glute Bridge", sets: 3, reps: "12", rest: 90, muscle: "glutes" },
        { name: "Ab Wheel", sets: 3, reps: "12", rest: 60, muscle: "core" },
      ],
    },
  ],
};

// ── Plan 5: Advanced — Strength ─────────────────────────────────────────────

const ADVANCED_STRENGTH: CuratedPlan = {
  id: "advanced_strength",
  i18nKey: "fitness.plan.advancedStrength",
  level: "advanced",
  goal: "strength",
  daysPerWeek: 5,
  progressionRule: "fitness.plan.progression.linear",
  days: [
    {
      name: "Squat Day", i18nKey: "fitness.plan.day.squatDay",
      exercises: [
        { name: "Squat", sets: 5, reps: "3-5", rest: 300, muscle: "quads" },
        { name: "Pause Squat", sets: 3, reps: "5", rest: 180, muscle: "quads" },
        { name: "Leg Press", sets: 4, reps: "8", rest: 120, muscle: "quads" },
        { name: "Leg Curl", sets: 3, reps: "10", rest: 90, muscle: "hamstrings" },
        { name: "Ab Wheel", sets: 3, reps: "10", rest: 60, muscle: "core" },
      ],
    },
    {
      name: "Bench Day", i18nKey: "fitness.plan.day.benchDay",
      exercises: [
        { name: "Bench Press", sets: 5, reps: "3-5", rest: 300, muscle: "chest" },
        { name: "Close-Grip Bench", sets: 3, reps: "6-8", rest: 180, muscle: "triceps" },
        { name: "Dumbbell Press", sets: 3, reps: "8-10", rest: 120, muscle: "chest" },
        { name: "Lateral Raise", sets: 3, reps: "15", rest: 60, muscle: "shoulders" },
        { name: "Tricep Pushdown", sets: 3, reps: "12", rest: 60, muscle: "triceps" },
      ],
    },
    {
      name: "Deadlift Day", i18nKey: "fitness.plan.day.deadliftDay",
      exercises: [
        { name: "Deadlift", sets: 5, reps: "3-5", rest: 300, muscle: "back" },
        { name: "Deficit Deadlift", sets: 3, reps: "5", rest: 180, muscle: "back" },
        { name: "Barbell Row", sets: 4, reps: "6-8", rest: 120, muscle: "back" },
        { name: "Weighted Pull-up", sets: 3, reps: "6-8", rest: 120, muscle: "back" },
        { name: "Barbell Curl", sets: 3, reps: "10", rest: 60, muscle: "biceps" },
      ],
    },
    {
      name: "OHP Day", i18nKey: "fitness.plan.day.ohpDay",
      exercises: [
        { name: "Overhead Press", sets: 5, reps: "3-5", rest: 300, muscle: "shoulders" },
        { name: "Push Press", sets: 3, reps: "5", rest: 180, muscle: "shoulders" },
        { name: "Incline Dumbbell Press", sets: 3, reps: "8-10", rest: 120, muscle: "chest" },
        { name: "Face Pull", sets: 3, reps: "15", rest: 60, muscle: "shoulders" },
        { name: "Lateral Raise", sets: 3, reps: "15", rest: 45, muscle: "shoulders" },
      ],
    },
    {
      name: "Accessories", i18nKey: "fitness.plan.day.accessories",
      exercises: [
        { name: "Front Squat", sets: 3, reps: "6-8", rest: 120, muscle: "quads" },
        { name: "Romanian Deadlift", sets: 3, reps: "8-10", rest: 120, muscle: "hamstrings" },
        { name: "Dumbbell Row", sets: 3, reps: "10-12", rest: 90, muscle: "back" },
        { name: "Cable Flyes", sets: 3, reps: "12-15", rest: 60, muscle: "chest" },
        { name: "Hammer Curl", sets: 3, reps: "12", rest: 60, muscle: "biceps" },
      ],
    },
  ],
};

// ── Plan 6: Advanced — Performance ──────────────────────────────────────────

const ADVANCED_PERFORMANCE: CuratedPlan = {
  id: "advanced_performance",
  i18nKey: "fitness.plan.advancedPerformance",
  level: "advanced",
  goal: "performance",
  daysPerWeek: 6,
  progressionRule: "fitness.plan.progression.periodized",
  days: [
    {
      name: "Power Upper", i18nKey: "fitness.plan.day.powerUpper",
      exercises: [
        { name: "Bench Press", sets: 5, reps: "3-5", rest: 240, muscle: "chest" },
        { name: "Weighted Pull-up", sets: 5, reps: "3-5", rest: 240, muscle: "back" },
        { name: "Push Press", sets: 4, reps: "5", rest: 180, muscle: "shoulders" },
        { name: "Barbell Row", sets: 4, reps: "5", rest: 180, muscle: "back" },
      ],
    },
    {
      name: "Power Lower", i18nKey: "fitness.plan.day.powerLower",
      exercises: [
        { name: "Squat", sets: 5, reps: "3-5", rest: 300, muscle: "quads" },
        { name: "Deadlift", sets: 4, reps: "3-5", rest: 300, muscle: "back" },
        { name: "Box Jump", sets: 4, reps: "5", rest: 120, muscle: "legs" },
        { name: "Glute Bridge", sets: 3, reps: "10", rest: 90, muscle: "glutes" },
      ],
    },
    {
      name: "Hypertrophy Upper", i18nKey: "fitness.plan.day.hypertrophyUpper",
      exercises: [
        { name: "Incline Dumbbell Press", sets: 4, reps: "10-12", rest: 90, muscle: "chest" },
        { name: "Cable Row", sets: 4, reps: "10-12", rest: 90, muscle: "back" },
        { name: "Lateral Raise", sets: 4, reps: "15", rest: 45, muscle: "shoulders" },
        { name: "Cable Flyes", sets: 3, reps: "12-15", rest: 60, muscle: "chest" },
        { name: "Barbell Curl", sets: 3, reps: "12", rest: 60, muscle: "biceps" },
        { name: "Tricep Pushdown", sets: 3, reps: "12", rest: 60, muscle: "triceps" },
      ],
    },
    {
      name: "Hypertrophy Lower", i18nKey: "fitness.plan.day.hypertrophyLower",
      exercises: [
        { name: "Front Squat", sets: 4, reps: "8-10", rest: 120, muscle: "quads" },
        { name: "Romanian Deadlift", sets: 4, reps: "10-12", rest: 90, muscle: "hamstrings" },
        { name: "Walking Lunges", sets: 3, reps: "12/leg", rest: 90, muscle: "legs" },
        { name: "Leg Extension", sets: 3, reps: "15", rest: 60, muscle: "quads" },
        { name: "Leg Curl", sets: 3, reps: "12", rest: 60, muscle: "hamstrings" },
        { name: "Calf Raise", sets: 4, reps: "15", rest: 45, muscle: "calves" },
      ],
    },
    {
      name: "Athletic", i18nKey: "fitness.plan.day.athletic",
      exercises: [
        { name: "Clean and Press", sets: 4, reps: "5", rest: 180, muscle: "shoulders" },
        { name: "Box Jump", sets: 4, reps: "6", rest: 120, muscle: "legs" },
        { name: "Kettlebell Swing", sets: 4, reps: "15", rest: 90, muscle: "glutes" },
        { name: "Battle Ropes", sets: 4, reps: "30s", rest: 60, muscle: "cardio" },
        { name: "Plank", sets: 3, reps: "60s", rest: 45, muscle: "core" },
      ],
    },
    {
      name: "Conditioning", i18nKey: "fitness.plan.day.conditioning",
      exercises: [
        { name: "Burpees", sets: 4, reps: "10", rest: 60, muscle: "cardio" },
        { name: "Mountain Climbers", sets: 4, reps: "30s", rest: 45, muscle: "core" },
        { name: "Jumping Jacks", sets: 4, reps: "45s", rest: 30, muscle: "cardio" },
        { name: "Sprint Intervals", sets: 6, reps: "30s", rest: 90, muscle: "cardio" },
        { name: "Ab Wheel", sets: 3, reps: "12", rest: 60, muscle: "core" },
      ],
    },
  ],
};

// ── Exports ─────────────────────────────────────────────────────────────────

export const CURATED_PLANS: CuratedPlan[] = [
  BEGINNER_WEIGHT_LOSS,
  BEGINNER_HYPERTROPHY,
  INTERMEDIATE_STRENGTH,
  INTERMEDIATE_HYPERTROPHY,
  ADVANCED_STRENGTH,
  ADVANCED_PERFORMANCE,
];


export const FITNESS_CURATED_PLAN_CATALOG_VERSION = '2026-10-07.v1' as const;
export const FITNESS_CURATED_PLAN_CATALOG_IDS = ['beginner_weight_loss', 'beginner_hypertrophy', 'intermediate_strength', 'intermediate_hypertrophy', 'advanced_strength', 'advanced_performance'] as const;
export type FitnessCuratedPlanCatalogId = typeof FITNESS_CURATED_PLAN_CATALOG_IDS[number];
export type FitnessCuratedPlanLanguage = Lang;
export interface FitnessCuratedPlanTemplate {
  catalog_id: FitnessCuratedPlanCatalogId; version: string; language: Lang; name: string; description: string; days: PlanDay[];
  goal: FitnessGoal; level: FitnessLevel; duration_weeks: 4; source: 'curated'; external_id: null;
}
export function fitnessCuratedPlanCatalogId(value: unknown): FitnessCuratedPlanCatalogId {
  if (typeof value !== 'string' || !(FITNESS_CURATED_PLAN_CATALOG_IDS as readonly string[]).includes(value)) throw new Error('invalid_catalog_id');
  return value as FitnessCuratedPlanCatalogId;
}
export function fitnessCuratedPlanLanguage(value: unknown): Lang {
  const language = typeof value === 'string' ? normalizeLang(value) : null;
  if (!language) throw new Error('invalid_language');
  return language;
}
export function materializeFitnessCuratedPlanTemplate(catalogId: unknown, languageCode: unknown): FitnessCuratedPlanTemplate {
  const catalog_id = fitnessCuratedPlanCatalogId(catalogId), language = fitnessCuratedPlanLanguage(languageCode);
  const plan = CURATED_PLANS.find(p => p.id === catalog_id)!;
  const labels = FITNESS_CURATED_PLAN_LABELS[language][catalog_id];
  return { catalog_id, version: FITNESS_CURATED_PLAN_CATALOG_VERSION, language, name: labels.name, description: labels.description,
    days: JSON.parse(JSON.stringify(plan.days)) as PlanDay[], goal: plan.goal, level: plan.level, duration_weeks: 4, source: 'curated', external_id: null };
}
